import type React from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";

// Define available themes
export const themes = [
	"light",
	"dark",
	"cupcake",
	"bumblebee",
	"emerald",
	"corporate",
	"synthwave",
	"retro",
	"cyberpunk",
	"valentine",
	"halloween",
	"garden",
	"forest",
	"aqua",
	"lofi",
	"pastel",
	"fantasy",
	"wireframe",
	"black",
	"luxury",
	"dracula",
	"cmyk",
	"autumn",
	"business",
	"acid",
	"lemonade",
	"night",
	"coffee",
	"winter",
] as const;

export type ThemeName = (typeof themes)[number];

// Define the context type
interface ThemeContextType {
	currentTheme: ThemeName;
	changeTheme: (theme: ThemeName) => void;
	themes: typeof themes;
}

// Create the context with a default value
const ThemeContext = createContext<ThemeContextType>({
	currentTheme: "light",
	changeTheme: () => {},
	themes,
});

// Storage key for persisting theme preference
const THEME_STORAGE_KEY = "pilatestime-theme";

// Provider component
export function ThemeProvider({ children }: { children: React.ReactNode }) {
	// Initialize with stored theme or default to light
	const [currentTheme, setCurrentTheme] = useState<ThemeName>(() => {
		const storedTheme = localStorage.getItem(
			THEME_STORAGE_KEY,
		) as ThemeName | null;
		return storedTheme && themes.includes(storedTheme as ThemeName)
			? (storedTheme as ThemeName)
			: "light";
	});

	// Change theme function - memoized with useCallback
	const changeTheme = useCallback((theme: ThemeName) => {
		if (themes.includes(theme)) {
			setCurrentTheme(theme);
			localStorage.setItem(THEME_STORAGE_KEY, theme);
			document.documentElement.setAttribute("data-theme", theme);
		}
	}, []);

	// Initialize theme on mount
	useEffect(() => {
		changeTheme(currentTheme);
	}, [currentTheme, changeTheme]);

	const value = {
		currentTheme,
		changeTheme,
		themes,
	};

	return (
		<ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
	);
}

// Custom hook to use the theme context
export function useTheme() {
	const context = useContext(ThemeContext);
	if (context === undefined) {
		throw new Error("useTheme must be used within a ThemeProvider");
	}
	return context;
}
